import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Host, { buildCanonicalHostPath } from '../components/Host';
import hostData from '../static/hostData';
import { useLegacyApi } from '../../../routes/legacyApiHook';
import { HOST } from '../../../routes/config/pageTypes';
import { RootState } from '../../../config/redux/types';
import useTrackEcommerce from '../../../features/tracking/useTrackEcommerce';
import { Accommodation, HostState, Property } from '../../../routes/reducers/types';
import { TrackViewItem } from '../../../features/tracking/types';

function buildTrackingIdentifier(
  lang: string | undefined,
  host: HostState | undefined,
): string | null {
  if (lang == null || host == null || host.accommodation == null) {
    return null;
  }
  return [lang, host.accommodation.id.toString()].join('-');
}

function buildTrackingItems(
  accommodation?: Accommodation,
  property?: Property,
): TrackViewItem[] | null {
  if (!accommodation || !property) {
    return null;
  }
  return [
    {
      id: accommodation.id.toString(),
      name: accommodation.display_title,
      item_id: accommodation.id.toString(),
      item_name: accommodation.display_title,
      list_name: 'accommodation_page',
      item_list_name: 'accommodation_page',
    },
  ];
}

export default function HostContainer(): JSX.Element | null {
  const { host, lang } = useParams<'host' | 'lang'>();
  const { routes, accommodationReviews } = useSelector((state: RootState) => state);
  const location = useLocation();
  const navigate = useNavigate();

  useLegacyApi([host], HOST);

  useTrackEcommerce(
    {
      event: 'view_item',
      currency: 'EUR',
      value: 0,
      items: buildTrackingItems(routes?.host?.accommodation, routes?.host?.property),
    },
    buildTrackingIdentifier(lang, routes.host),
  );

  const accommodation = routes.host?.accommodation;

  function redirectToCanonicalPath(acc: Accommodation, language: string): boolean {
    const canonicalPath = acc && language ? buildCanonicalHostPath(acc, language) : null;

    if (canonicalPath != null && location.pathname !== canonicalPath) {
      let navigateTo = canonicalPath;
      if (location.search) {
        navigateTo = `${navigateTo}${location.search}`;
      }
      navigate(navigateTo);
      return true;
    }

    return false;
  }

  useEffect(() => {
    if (!accommodation || !lang) {
      return;
    }
    redirectToCanonicalPath(accommodation, lang);
  }, [accommodation, lang]);

  if (!routes.host) {
    return <Host showReviews={false} locale={routes.locale} />;
  }

  const showReviews =
    !!accommodationReviews &&
    !!accommodationReviews.pagination &&
    accommodationReviews.pagination.totalEntries > 0;
  const routeHost = routes.host;

  if (routeHost == null || lang == null) {
    return null;
  }

  return (
    <Host
      sectionTitle={hostData().sectionTitle}
      property={routeHost.property}
      related={routeHost.related}
      accommodation={accommodation}
      meta={routeHost.meta}
      showReviews={showReviews}
      locale={lang}
    />
  );
}
